import React, { useState, useEffect } from "react";
import { Space, message, Pagination } from "antd";
import Layouts from "../../components/Layouts";
import { useSelector } from "react-redux";
import validarpermiso, { formatPrice } from "../../lib/utils";
import SearchSection from "./components/SearchSection";
import InvoiceTable from "./components/InvoiceTable";
import InvoiceModal from "./components/InvoiceModal";
import InfoTags from "./components/InfoTags";
import { Form } from "antd";
import FacturasService from "../../services/facturas";


export default function ListFactura() {
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);
  const permisos = useSelector((state) => state.permisos);
  const userBodega = useSelector((state) => state.userdata.bodega?.id);
  const signoMoneda = useSelector(state => state.signoMoneda.signo_moneda);
  // Add pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Add new state variables
  const [searchForm] = Form.useForm();
  const [filters, setFilters] = useState({
    consecutivo: "",
    vendedor: null,
    fecha: null,
    grupo: null,
  });

  // State for summary data
  const [summaryData, setSummaryData] = useState({
    totalFacturas: 0,
    sumaTotal: 0,
    totalSeriales: 0,
  });

  // Function to calculate summary data
  const calculateSummaryData = async (filterObj, searchValues = null) => {
    try {
      const summaryData = await FacturasService.getFacturasSummary(filterObj);
      
      // Get seriales count for all facturas using the new method
      let totalSeriales = 0;
      if (summaryData.totalFacturas > 0) {
        // We need to get the facturas first to extract their IDs
        const facturasResponse = await FacturasService.getFacturasTableData({
          filterObj,
          page: 1,
          pageSize: -1 // Get all facturas to count seriales
        });
        
        if (facturasResponse && facturasResponse.length > 0) {
          const facturaIds = facturasResponse.map(factura => factura.id);
          // Use searchValues directly if provided, otherwise fall back to filters state
          const grupoId = searchValues ? searchValues.grupo : filters.grupo;
          totalSeriales = await FacturasService.getSerialesCountByFacturas(facturaIds, "2", grupoId);
        }
      }
      
      // Update summary data with the new seriales count
      const updatedSummaryData = {
        ...summaryData,
        totalSeriales: totalSeriales
      };
      
      setSummaryData(updatedSummaryData);
    } catch (error) {
      console.error("Error calculating summary data:", error);
      setSummaryData({
        totalFacturas: 0,
        sumaTotal: 0,
        totalSeriales: 0,
      });
    }
  };

  // Update fetchFacturas to handle pagination and include search filters
  const fetchFacturas = async (page = 1, pageSize = 10) => {
    if (!validarpermiso("facturas", "read", permisos)) {
      setHasPermissionError(true);
      return;
    }

    setLoading(true);
    try {
      // Build filter object
      const filterObj = FacturasService.buildFilterObject(filters, userBodega);

      // Get total count with filters
      const totalItems = await FacturasService.getFacturasCount(filterObj);

      // Get filtered and paginated data - optimized for table view
      const response = await FacturasService.getFacturasTableData({
        filterObj,
        page,
        pageSize
      });

      setFacturas(response);
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
        total: totalItems,
      });
      setHasPermissionError(false);

      // Calculate summary data
      await calculateSummaryData(filterObj, filters);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      if (error.response?.status === 403) {
        setHasPermissionError(true);
        message.error("No tiene permisos para ver facturas");
      }
    } finally {
      setLoading(false);
    }
  };

  // Update handleSearch to use search values directly
  const handleSearch = async (values) => {
    try {
      setLoading(true);

      // Build filter object with the values directly
      const filterObj = FacturasService.buildFilterObject(values, userBodega);

      // Get total count with filters
      const totalItems = await FacturasService.getFacturasCount(filterObj);

      // Get filtered data - optimized for table view
      const response = await FacturasService.getFacturasTableData({
        filterObj,
        page: 1,
        pageSize: pagination.pageSize
      });

      setFilters(values);
      setFacturas(response);
      setPagination({
        ...pagination,
        current: 1,
        total: totalItems,
      });

      // Calculate summary data
      await calculateSummaryData(filterObj, values);
    } catch (error) {
      console.error("Error searching invoices:", error);
      message.error("Error al buscar facturas");
    } finally {
      setLoading(false);
    }
  };

  // Update useEffect to use pagination
  useEffect(() => {
    fetchFacturas(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize]);



  const showFacturaDetails = (factura) => {
    setSelectedFactura(factura);
    setIsModalVisible(true);
  };

  // Add this before the return statement
  const handleClear = async () => {
    try {
      setLoading(true);
      searchForm.resetFields();

      // Reset to initial filter with only bodega
      const filterObj = FacturasService.buildFilterObject({}, userBodega);

      // Get total count with reset filters
      const totalItems = await FacturasService.getFacturasCount(filterObj);

      // Get fresh data - optimized for table view
      const response = await FacturasService.getFacturasTableData({
        filterObj,
        page: 1,
        pageSize: pagination.pageSize
      });

      // Reset all states
      setFilters({
        consecutivo: "",
        vendedor: null,
        fecha: null,
        grupo: null,
      });
      setFacturas(response);
      setPagination({
        ...pagination,
        current: 1,
        total: totalItems,
      });

      // Calculate summary data with reset filter
      await calculateSummaryData(filterObj, {});
    } catch (error) {
      console.error("Error resetting data:", error);
      message.error("Error al restablecer los datos");
    } finally {
      setLoading(false);
    }
  };



  return (
    <Layouts>
      {!hasPermissionError ? (
        <Space direction="vertical" style={{ width: "100%" }}>
          <SearchSection
            form={searchForm}
            onSearch={handleSearch}
            onClear={handleClear}
          />

          <InfoTags
            info={[
              `Total de Facturas: ${summaryData.totalFacturas}`,
              `Total de Seriales: ${summaryData.totalSeriales}`,
              `Suma Total: ${formatPrice(summaryData.sumaTotal, signoMoneda)}`,
            ]}
          />

          <InvoiceTable
            facturas={facturas}
            loading={loading}
            showFacturaDetails={showFacturaDetails}
            fetchFacturas={() =>
              fetchFacturas(pagination.current, pagination.pageSize)
            }
          />

          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger={true}
            showQuickJumper={true}
            showTotal={(total) => `Total: ${total} facturas`}
            pageSizeOptions={["10", "25", "50", "100"]}
            onChange={(page, pageSize) => {
              setPagination({
                ...pagination,
                current: page,
                pageSize: pageSize,
              });
            }}
            style={{ marginTop: 16, textAlign: "right" }}
            disabled={!validarpermiso("facturas", "read", permisos)}
          />

          <InvoiceModal
            selectedFactura={selectedFactura}
            isModalVisible={isModalVisible}
            setIsModalVisible={setIsModalVisible}
            setSelectedFactura={setSelectedFactura}
            fetchFacturas={() => fetchFacturas(pagination.current, pagination.pageSize)}
         validation={true}
         />
        </Space>
      ) : (
        <h3>No tiene permisos para ver facturas</h3>
      )}
    </Layouts>
  );
}
